import React, { useEffect, useState, useCallback, useContext, useRef } from 'react'
import LoadMoreButton from '../../../../components/LoadMoreButton'
import { useOutletContext, useNavigate } from 'react-router-dom'
import axios from '../../../../config/axios'
import SectionContext from '../../../../contexts/SectionContext'
import UserContext from '../../../../contexts/UserContext'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import lookupIcon from '../../../../assets/equipment/Tables/lookup.svg'
import pencilIcon from '../../../../assets/equipment/Tables/pencil.svg'
import trashIcon from '../../../../assets/equipment/Tables/trash.svg'
import { CollectionsOutletType } from '../index'
import ViewCollectionModal from '../ViewCollectionModal'
import ConfirmationModal from '../../../../components/ModalConfirmation'
import {
  CollectionsTableContainer,
  TableWrapper,
  InfoMessage,
} from './style'
import {
  GlobalTable,
  HeaderLine,
  Th,
  Tr,
  Td,
  TdButtons,
  DivInTd,
} from '../../../../components/styles/table-style'

import styled from 'styled-components'
import { useTooltip } from '../../../../components/Tooltip/useTooltip'

const CellContainer = styled.div`
  max-width: 220px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: help;
  width: 100%;
`;


const GATEWAY_URL = process.env.REACT_APP_GATEWAY_BASE_URL
const FASHION_API_URL = `${GATEWAY_URL}/backend-fashion-orders/fashion-product`

interface CollectionData {
  id: number
  collectionName: string
  collectionColor: string
  families: string
  grades: string
  groups: string
  themes: string
  fabrics: string
  trimmings: string
}

const CollectionsTable: React.FC = () => {
  const { refresh, search, setIsLoading } = useOutletContext<CollectionsOutletType>()
  const navigate = useNavigate()

  const [collections, setCollections] = useState<CollectionData[]>([])
  const [visibleCount, setVisibleCount] = useState<number>(50)
  const [totalCollections, setTotalCollections] = useState<number | undefined>(undefined)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [showViewModal, setShowViewModal] = useState<boolean>(false)
  const [selectedCollection, setSelectedCollection] = useState<CollectionData | null>(null)
  const [deleteCollectionId, setDeleteCollectionId] = useState<number | null>(null)
  const cellRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})

  const { tooltipElement, handleMouseEnter, handleMouseLeave } = useTooltip({
    maxWidth: 360,
    maxHeight: 280,
    rowHeight: 22,
    padding: 16,
    verticalGap: 4,
  })

  const { permissions } = useContext(SectionContext)
  const { user } = useContext(UserContext)
  const { setFeedback } = useContext(FeedbackContext)

  const canDeleteCollection = useCallback((): boolean => {
    if (user?.role === 'ADMIN_GLOBAL' || user?.adminUser) return true

    const perms: string[] = Array.isArray(permissions) ? permissions : []
    const allowedKeys = [
      'collections.delete',
      'delete-collections',
      'delete_collections',
      'collections',
    ]
    return allowedKeys.some((k) => perms.includes(k))
  }, [permissions, user])

  const fetchData = useCallback(async () => {
    setLoading(true)
    if (setIsLoading) setIsLoading(true)
    setError(null)

    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${FASHION_API_URL}/collections`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          limit: visibleCount,
          offset: 0,
          search: search || undefined,
        },
      })


      let backendData: any[] = []
      if (Array.isArray(response.data)) {
        backendData = response.data
        setTotalCollections(undefined)
      } else if (response.data && Array.isArray(response.data.content)) {
        backendData = response.data.content
        const total = response.data.totalElements
        setTotalCollections(typeof total === 'number' ? total : undefined)
      }

      console.debug('[CollectionsTable] backendData raw:', JSON.stringify(backendData, null, 2))
      let fetchedCollections: CollectionData[] = await Promise.all(
        backendData.map(async (item: any) => {
          const gradeNames = (item.GradeInCollection || [])
            .map((g: any) => g.Grade?.name)
            .filter(Boolean)
          const gradesStr = gradeNames.length > 0 ? gradeNames.join(', ') : '-'

          const groupNames = (item.GroupInCollection || [])
            .map((g: any) => g.Group?.name)
            .filter(Boolean)
          const groupsStr = groupNames.length > 0 ? groupNames.join(', ') : '-'

          const fabricNames = (item.CollectionMaterial || [])
            .map((cm: any) => cm.Material?.name)
            .filter(Boolean)
          const fabricsStr = fabricNames.length > 0 ? fabricNames.join(', ') : '-'

          const trimmingNames = (item.CollectionMaterial || [])
            .flatMap((cm: any) =>
              (cm.CollectionMaterialResources || [])
                .flatMap((r: any) => {
                  if (Array.isArray(r.resources) && r.resources.length > 0) {
                    return r.resources
                      .map((res: any) => res?.name)
                      .filter((name: any): name is string => !!name)
                  }

                  const materialName = r.Material?.name
                  return [
                    materialName ||
                    (r.materialId ? `Material #${r.materialId}` : undefined) ||
                    r.Category_Resource?.name ||
                    `Registro #${r.id}`,
                  ]
                })
                .filter(Boolean),
            )
          const uniqueTrimmings = [...new Set(trimmingNames)]
          const trimmingsStr = uniqueTrimmings.length > 0 ? uniqueTrimmings.join(', ') : '-'

          console.debug('[CollectionsTable] collection', item.id, 'uniqueTrimmings', uniqueTrimmings, 'trimmingNames', trimmingNames)

          const collectionColorNames = (item.CollectionColor || [])
            .map((cc: any) => cc.name || cc.CollectionColor?.name)
            .filter(Boolean)
          const collectionColorStr = collectionColorNames.length > 0 ? [...new Set(collectionColorNames)].join(', ') : '-'

          const familyNames = (item.Family || [])
            .map((f: any) => f.name)
            .filter(Boolean)
          const familiesStr = familyNames.length > 0 ? familyNames.join(', ') : '-'

          const themeItems = item.collectionThemes || item.CollectionTheme || []
          const themeNames = themeItems
            .map((ct: any) => ct.Theme?.name)
            .filter(Boolean)
          const themesStr = themeNames.length > 0 ? themeNames.join(', ') : '-'

          return {
            id: item.id,
            collectionName: item.name || 'Sem nome',
            collectionColor: collectionColorStr,
            families: familiesStr,
            grades: gradesStr,
            groups: groupsStr,
            themes: themesStr,
            fabrics: fabricsStr,
            trimmings: trimmingsStr,
          }
        }),
      )

      fetchedCollections.sort((a, b) => b.id - a.id)

      if (search) {
        const searchLower = search.toLowerCase()
        fetchedCollections = fetchedCollections.filter(
          (item) =>
            item.collectionName.toLowerCase().includes(searchLower) ||
            item.collectionColor.toLowerCase().includes(searchLower) ||
            item.families.toLowerCase().includes(searchLower) ||
            item.grades.toLowerCase().includes(searchLower) ||
            item.groups.toLowerCase().includes(searchLower) ||
            item.themes.toLowerCase().includes(searchLower) ||
            item.fabrics.toLowerCase().includes(searchLower) ||
            item.trimmings.toLowerCase().includes(searchLower),
        )
      }

      setCollections(fetchedCollections)
      setLoading(false)
      if (setIsLoading) setIsLoading(false)
    } catch (err: any) {
      console.error('Erro ao buscar coleções:', err)
      setError(
        err.response?.data?.message ||
        'Erro ao carregar coleções. Tente novamente.',
      )
      setLoading(false)
      if (setIsLoading) setIsLoading(false)
    }
  }, [search, setIsLoading, visibleCount])

  useEffect(() => {
    fetchData()
  }, [fetchData, refresh, visibleCount])

  const handleView = (collection: CollectionData) => {
    setSelectedCollection(collection)
    setShowViewModal(true)
  }

  const handleCellHover = (e: React.MouseEvent, content: string, key: string, id: number) => {
    const ref = cellRefs.current[`${key}-${id}`]
    if (ref && content) {
      const isOverflowing = ref.scrollWidth > ref.clientWidth
      const items = content.split(',').map(s => s.trim()).filter(Boolean)
      if (isOverflowing || items.length > 1) {
        const tooltipText = items.length > 1 ? items.join('\n') : content
        handleMouseEnter(e, tooltipText)
      }
    }
  }

  const getFirstWithEllipsis = (content: string) => {
    if (!content) return '-'
    const parts = content.split(',').map(s => s.trim()).filter(Boolean)
    if (parts.length === 0) return '-'
    if (parts.length === 1) return parts[0]
    return `${parts[0]}...`
  }

  const handleEdit = (collection: CollectionData) => {
    navigate(`edit/${collection.id}`)
  }

  const handleDelete = async (id: number) => {
    if (!canDeleteCollection()) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Você não tem permissão para deletar coleções. Apenas usuários autorizados podem realizar esta ação.',
      })
      return
    }

    setDeleteCollectionId(id)
  }

  const handleConfirmDelete = async () => {
    if (!deleteCollectionId) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${FASHION_API_URL}/collections/${deleteCollectionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      setFeedback({ isOpen: true, statusOk: true, message: 'Coleção excluída com sucesso!' })
      setDeleteCollectionId(null)
      fetchData()
    } catch (err: any) {
      console.error('Erro ao deletar coleção:', err)
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: err.response?.data?.message || 'Erro ao deletar coleção. Tente novamente.',
      })
    }
  }

  const handleCancelDelete = () => {
    setDeleteCollectionId(null)
  }

  if (loading) {
    return (
      <CollectionsTableContainer>
        <InfoMessage>
          <p>Carregando coleções...</p>
        </InfoMessage>
      </CollectionsTableContainer>
    )
  }

  if (error) {
    return (
      <CollectionsTableContainer>
        <InfoMessage style={{ color: 'red' }}>
          <p>{error}</p>
        </InfoMessage>
      </CollectionsTableContainer>
    )
  }

  if (collections.length === 0) {
    return (
      <CollectionsTableContainer>
        <InfoMessage>
          <p>Nenhuma coleção encontrada.</p>
        </InfoMessage>
      </CollectionsTableContainer>
    )
  }

  return (
    <CollectionsTableContainer>
      <TableWrapper>
        <GlobalTable>
          <thead>
            <HeaderLine>
              <Th>Coleção</Th>
              <Th>Cor</Th>
              <Th>Famílias</Th>
              <Th>Grades</Th>
              <Th>Grupos</Th>
              <Th>Temas</Th>
              <Th>Tecidos</Th>
              <Th>Aviamentos</Th>
              <Th></Th>
            </HeaderLine>
          </thead>
          <tbody>
            {collections.map((collection) => (
              <Tr key={collection.id}>
                <Td>
                  <CellContainer
                    ref={(el) => (cellRefs.current[`collectionName-${collection.id}`] = el)}
                    onMouseEnter={(e: any) => handleCellHover(e, collection.collectionName, 'collectionName', collection.id)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {collection.collectionName}
                  </CellContainer>
                </Td>
                <Td>
                  <CellContainer
                    ref={(el) => (cellRefs.current[`collectionColor-${collection.id}`] = el)}
                    onMouseEnter={(e: any) => handleCellHover(e, collection.collectionColor, 'collectionColor', collection.id)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {getFirstWithEllipsis(collection.collectionColor)}
                  </CellContainer>
                </Td>
                <Td>
                  <CellContainer
                    ref={(el) => (cellRefs.current[`families-${collection.id}`] = el)}
                    onMouseEnter={(e: any) => handleCellHover(e, collection.families, 'families', collection.id)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {getFirstWithEllipsis(collection.families)}
                  </CellContainer>
                </Td>
                <Td>
                  <CellContainer
                    ref={(el) => (cellRefs.current[`grades-${collection.id}`] = el)}
                    onMouseEnter={(e: any) => handleCellHover(e, collection.grades, 'grades', collection.id)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {getFirstWithEllipsis(collection.grades)}
                  </CellContainer>
                </Td>
                <Td>
                  <CellContainer
                    ref={(el) => (cellRefs.current[`groups-${collection.id}`] = el)}
                    onMouseEnter={(e: any) => handleCellHover(e, collection.groups, 'groups', collection.id)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {getFirstWithEllipsis(collection.groups)}
                  </CellContainer>
                </Td>
                <Td>
                  <CellContainer
                    ref={(el) => (cellRefs.current[`themes-${collection.id}`] = el)}
                    onMouseEnter={(e: any) => handleCellHover(e, collection.themes, 'themes', collection.id)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {getFirstWithEllipsis(collection.themes)}
                  </CellContainer>
                </Td>
                <Td>
                  <CellContainer
                    ref={(el) => (cellRefs.current[`fabrics-${collection.id}`] = el)}
                    onMouseEnter={(e: any) => handleCellHover(e, collection.fabrics, 'fabrics', collection.id)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {getFirstWithEllipsis(collection.fabrics)}
                  </CellContainer>
                </Td>
                <Td>
                  <CellContainer
                    ref={(el) => (cellRefs.current[`trimmings-${collection.id}`] = el)}
                    onMouseEnter={(e: any) => handleCellHover(e, collection.trimmings, 'trimmings', collection.id)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {getFirstWithEllipsis(collection.trimmings)}
                  </CellContainer>
                </Td>
                <TdButtons>
                  <DivInTd className='hover-blue' onClick={() => handleView(collection)} title='Visualizar'>
                    <img src={lookupIcon} alt='Visualizar' />
                  </DivInTd>
                  <DivInTd className='hover-blue' onClick={() => handleEdit(collection)} title='Editar'>
                    <img src={pencilIcon} alt='Editar' />
                  </DivInTd>
                  <DivInTd className='hover-red' onClick={() => handleDelete(collection.id)} title='Excluir'>
                    <img src={trashIcon} alt='Excluir' />
                  </DivInTd>
                </TdButtons>
              </Tr>
            ))}
          </tbody>
        </GlobalTable>
        <LoadMoreButton
          currentCount={collections.length}
          totalCount={totalCollections}
          hasMore={typeof totalCollections === 'number' ? collections.length < totalCollections : false}
          onLoadMore={() => setVisibleCount((prev) => prev + 50)}
        />
      </TableWrapper>

      {tooltipElement}

      {selectedCollection && (
        <ViewCollectionModal
          collectionId={selectedCollection.id}
          onClose={() => {
            setShowViewModal(false);
            setSelectedCollection(null);
            fetchData();
          }}
        />
      )}

      <ConfirmationModal
        isOpen={deleteCollectionId !== null}
        title='Confirmar exclusão'
        message='Tem certeza que deseja excluir esta coleção? Esta ação não pode ser desfeita.'
        actions={[
          { label: 'Cancelar', onClick: handleCancelDelete, variant: 'outline' },
          { label: 'Excluir', onClick: handleConfirmDelete, variant: 'danger' },
        ]}
      />

    </CollectionsTableContainer>
  )
}

export default CollectionsTable